/**
 * Supabase-hosted project photography for 4 Seasons Painting.
 *
 * Every image on the site is a real photo from the client's own portfolio.
 * `photo()` returns a resized/compressed render URL so cards and heroes only
 * download what they actually display.
 *
 * The render endpoint content-negotiates on `Accept`, so browsers that advertise
 * WebP get WebP automatically and everyone else still gets a valid JPEG — no
 * build-time image pipeline needed.
 */

const BUCKET = "client-media/4-seasons-painting/images";

export const SUPABASE_RENDER = `https://sbdjxsxpkgngbkdabugx.supabase.co/storage/v1/render/image/public/${BUCKET}`;

/**
 * Resized variant — use for anything rendered in-page.
 *
 * Sources are 4032x3024 (12MP), so there is plenty of headroom: request roughly
 * 2x the CSS display size and keep quality high enough that heroes stay crisp
 * on retina screens. Anything below ~quality 78 visibly softens stucco texture.
 */
export const photo = (file: string, width = 1200, height = 900, quality = 80) =>
  `${SUPABASE_RENDER}/${file}?width=${width}&height=${height}&resize=cover&quality=${quality}`;

/**
 * Uncropped variant, scaled to fit inside a square box.
 *
 * `photo()` uses `resize=cover`, which is right for anything rendered with
 * `object-fit: cover` but would crop the portrait shots in the gallery. Use
 * this wherever the whole frame has to stay visible.
 */
export const photoContain = (file: string, size = 1600, quality = 82) =>
  `${SUPABASE_RENDER}/${file}?width=${size}&height=${size}&resize=contain&quality=${quality}`;

export interface ResponsivePhoto {
  /** Default `src` — the largest step, for browsers that ignore srcset. */
  src: string;
  /** Width-descriptor candidates so the browser can pick by viewport and DPR. */
  srcSet: string;
  /** Intrinsic dimensions of `src`, used to reserve layout space. */
  width: number;
  height: number;
}

/**
 * Builds a width-descriptor srcset for a single source photo.
 *
 * A full-bleed hero rendered at one fixed size means phones download a
 * desktop-sized image; the ladder below lets each device take only what it can
 * actually show.
 */
export const responsivePhoto = (
  file: string,
  widths: number[],
  aspect: number,
  quality = 80,
): ResponsivePhoto => {
  const height = (w: number) => Math.round(w / aspect);
  const largest = widths[widths.length - 1];
  return {
    src: photo(file, largest, height(largest), quality),
    srcSet: widths.map((w) => `${photo(file, w, height(w), quality)} ${w}w`).join(", "),
    width: largest,
    height: height(largest),
  };
};

/**
 * Named photos used outside the gallery. Each file appears exactly once across
 * the whole site (heroes, service cards, blog cards) so no two sections share
 * an image.
 */

/** Widths for edge-to-edge hero images (`sizes="100vw"`). */
const HERO_WIDTHS = [640, 960, 1280, 1440, 1600, 2000];

/**
 * Measured against quality 82 at 1440px: 3.1/255 mean pixel difference for 39%
 * fewer bytes, and the curve flattens below this. Heroes also sit under a
 * 0.8-opacity dark gradient, which hides encoder artefacts that would show on
 * a bare photo.
 */
const HERO_QUALITY = 74;

/** Finished two-storey exterior repaint with driveway and desert landscaping. */
export const heroPhotoSet = responsivePhoto("1785824035860-879-IMG_8619.jpeg", HERO_WIDTHS, 16 / 9, HERO_QUALITY);

/** Crew mid-project with ladders up — reads as "this is the work". */
export const servicesHeroPhotoSet = responsivePhoto("1785823960906-268-IMG_8533.jpeg", HERO_WIDTHS, 16 / 9, HERO_QUALITY);

/** Prep stage on a stucco exterior, ladders and masking visible. */
export const aboutHeroPhotoSet = responsivePhoto("1785823944420-514-IMG_8521.jpeg", HERO_WIDTHS, 16 / 9, HERO_QUALITY);
